/**
 * Centralized data sanitization for all Contentful content
 * Apply security checks before storing data in Redux
 */

// 1. Validate and sanitize URLs
const isTrustedSvgHost = (url) => {
  try {
    const host = new URL(url).hostname.toLowerCase();
    return host === 'ctfassets.net' || host.endsWith('.ctfassets.net');
  } catch {
    return false;
  }
};

export const sanitizeUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return null;
  }

  // Fix protocol-relative URLs (Contentful default)
  if (url.startsWith('//')) {
    url = `https:${url}`;
  }

  // Block dangerous protocols
  // eslint-disable-next-line no-script-url
  const dangerousProtocols = /^(javascript|data|vbscript|file):/i;
  if (dangerousProtocols.test(url)) {
    console.warn(`[SECURITY] Blocked malicious URL: ${url}`);
    return null;
  }

  // Block SVG files (common XSS vector) unless from trusted source
  if (url.toLowerCase().includes('.svg') && !isTrustedSvgHost(url)) {
    console.warn(`[SECURITY] Blocked untrusted SVG file: ${url}`);
    return null;
  }

  // Only allow http/https
  if (!url.match(/^https?:\/\//i) && !url.startsWith('/')) {
    console.warn(`[SECURITY] Blocked invalid URL protocol: ${url}`);
    return null;
  }

  return url;
};

// 2. Validate string length and content
export const sanitizeString = (str, maxLength = 1000) => {
  if (!str || typeof str !== 'string') {
    return '';
  }

  // Trim and limit length
  const trimmed = str.trim();
  if (trimmed.length > maxLength) {
    console.warn(`[SECURITY] String exceeds max length (${maxLength})`);
    return trimmed.substring(0, maxLength);
  }

  // Remove control characters (optional but good for strictness)
  // eslint-disable-next-line no-control-regex
  return trimmed.replace(/[\x00-\x1F\x7F]/g, "");
};

// 3. Sanitize rich text content from Contentful
export const sanitizeRichText = (richTextDoc) => {
  if (!richTextDoc || typeof richTextDoc !== 'object') {
    return null;
  }

  try {
    // Create a deep copy to avoid mutating original references
    const sanitized = JSON.parse(JSON.stringify(richTextDoc));

    // Recursively sanitize all nodes
    const sanitizeNode = (node) => {
      if (!node || typeof node !== 'object') return node;

      // Sanitize hyperlinks
      if (node.nodeType === 'hyperlink') {
        const uri = node.data?.uri;
        if (uri) {
          const cleanUri = sanitizeUrl(uri);
          if (!cleanUri) {
            // If URL is bad, convert link to plain text
            return {
              nodeType: 'text',
              value: node.content?.[0]?.value || '[Link Removed]',
              marks: []
            };
          }
          node.data.uri = cleanUri;
        }
      }

      // Sanitize embedded assets
      if (node.nodeType === 'embedded-asset-block') {
        const url = node.data?.target?.fields?.file?.url;
        if (url) {
          const sanitizedUrl = sanitizeUrl(url);
          if (!sanitizedUrl) {
            return null; // Remove the node entirely
          }
          node.data.target.fields.file.url = sanitizedUrl;
        }
      }

      // Recursively sanitize child content
      if (Array.isArray(node.content)) {
        node.content = node.content
          .map(child => sanitizeNode(child))
          .filter(child => child !== null);
      }

      return node;
    };

    // Start sanitization from root content
    if (Array.isArray(sanitized.content)) {
      sanitized.content = sanitized.content
        .map(node => sanitizeNode(node))
        .filter(node => node !== null);
    }

    return sanitized;
  } catch (error) {
    console.error('[SECURITY] Error sanitizing rich text:', error);
    return null;
  }
};

// Helper for arrays of rich text (used in Services)
export const sanitizeRichTextArray = (richTextArray) => {
  if (!Array.isArray(richTextArray)) return [];
  return richTextArray.map(item => sanitizeRichText(item)).filter(Boolean);
};

// 4. Specific Data Structure Sanitizers

export const sanitizeAboutUsBanner = (banner) => {
  if (!banner || typeof banner !== 'object') return {};
  return {
    AboutUsTitle: sanitizeString(banner.AboutUsTitle, 200),
    AboutUsContentText: sanitizeRichText(banner.AboutUsContentText),
    BannerImage: sanitizeUrl(banner.BannerImage)
  };
};

export const sanitizeAboutUsContent = (contentArray) => {
  if (!Array.isArray(contentArray)) return [];
  return contentArray.slice(0, 50).map(item => ({
    Title: sanitizeString(item.Title, 200),
    Content: sanitizeRichText(item.Content),
    AboutUsImage: sanitizeUrl(item.AboutUsImage)
  }));
};

export const sanitizeServices = (services) => {
  if (!Array.isArray(services)) return [];
  return services.slice(0, 50).map(service => ({
    Title: sanitizeString(service.Title, 200),
    // Handle nested image structure from Contentful
    Image: sanitizeUrl(service.Image || service.image?.fields?.file?.url),
    Icon: sanitizeUrl(service.Icon || service.icon?.fields?.file?.url),
    Slug: sanitizeString(service.Slug || service.slug, 100),
    Desc: sanitizeRichTextArray(service.Desc)
  }));
};

export const sanitizeRates = (rates) => {
  if (!Array.isArray(rates)) return [];
  return rates.slice(0, 100).map(rate => ({
    Title: sanitizeString(rate.Title, 200),
    File: sanitizeUrl(rate.File)
  })).filter(r => r.Title && r.File);
};

export const sanitizeCertificates = (certificates) => {
  if (!Array.isArray(certificates)) return [];
  return certificates.slice(0, 50).map(cert => ({
    image: sanitizeUrl(cert.image)
  })).filter(c => c.image);
};

export const sanitizeContact = (contact) => {
  if (!contact || typeof contact !== 'object') return {};
  const sanitized = {};

  Object.keys(contact).forEach(key => {
    const value = contact[key];

    if (Array.isArray(value)) {
      if (value.length === 0) {
        sanitized[key] = [];
      } else if (typeof value[0] === 'object') {
        // Check if it's email objects (have 'email' property)
        if (value[0].email !== undefined) {
          sanitized[key] = value.map(item => ({
            email: sanitizeString(item.email, 100),
            type: sanitizeString(item.type, 50)
          })).filter(item => item.email); // Remove empty emails
        } 
        // Check if it's phone objects (have 'Title', 'Number', or 'Phone' property)
        else if (value[0].Title !== undefined || value[0].Number !== undefined || value[0].Phone !== undefined) {
          sanitized[key] = value.map(p => ({
            Title: sanitizeString(p.Title, 100),
            Number: sanitizeString(p.Number || p.Phone, 50),
            Phone: sanitizeString(p.Phone || p.Number, 50)
          }));
        }
        // Generic object handling
        else {
          sanitized[key] = value.map(item => {
            const sanitizedItem = {};
            Object.keys(item).forEach(itemKey => {
              if (typeof item[itemKey] === 'string') {
                sanitizedItem[itemKey] = sanitizeString(item[itemKey], 200);
              } else {
                sanitizedItem[itemKey] = item[itemKey];
              }
            });
            return sanitizedItem;
          });
        }
      } else {
        // Array of strings
        sanitized[key] = value.map(s => sanitizeString(s, 200));
      }
    } else if (typeof value === 'string') {
      sanitized[key] = sanitizeString(value, 1000);
    } else {
      sanitized[key] = value;
    }
  });
  return sanitized;
};

export const sanitizeFAQ = (faqs) => {
  if (!Array.isArray(faqs)) return [];
  return faqs.slice(0, 100).map(faq => ({
    Question: sanitizeString(faq.Question, 500),
    Answer: sanitizeRichText(faq.Answer)
  }));
};

export const sanitizeLocations = (locations) => {
  if (!Array.isArray(locations)) return [];
  return locations.slice(0, 50).map(loc => {
    const cleanLoc = {};
    Object.keys(loc).forEach(key => {
      if (typeof loc[key] === 'string') {
        if (key.toLowerCase().includes('image') || key.toLowerCase().includes('url')) {
          cleanLoc[key] = sanitizeUrl(loc[key]);
        } else {
          cleanLoc[key] = sanitizeString(loc[key], 500);
        }
      } else {
        cleanLoc[key] = loc[key];
      }
    });
    return cleanLoc;
  });
};

// --- MAIN ENTRY POINT ---
export const sanitizeAPIResponse = (data) => {
  if (!data || typeof data !== 'object') {
    console.error('[SECURITY] Invalid API response data');
    return {};
  }

  console.log('[SECURITY] Starting data sanitization...');

  // Track sanitization failures
  const failures = {
    Location: { original: (data.Location || []).length, sanitized: 0 },
    Services: { original: (data.Services || []).length, sanitized: 0 },
    Faq: { original: (data.Faq || []).length, sanitized: 0 },
    CphRates: { original: (data.CphRates || []).length, sanitized: 0 },
    BillundRates: { original: (data.BillundRates || []).length, sanitized: 0 },
    AboutUsContent: { original: (data.AboutUsContent || []).length, sanitized: 0 },
    Certificates: { original: (data.Certificates || []).length, sanitized: 0 }
  };

  const sanitized = {
    PageTitle: sanitizeString(data.PageTitle, 200),
    BannerText: sanitizeRichText(data.BannerText),  // Rich text document
    HomeText: sanitizeString(data.HomeText, 500),   // Plain string
    HomeBannerText: sanitizeString(data.HomeBannerText, 500),  // Plain string
    Image: sanitizeUrl(data.Image),
    
    // Arrays
    Location: sanitizeLocations(data.Location || []),
    Services: sanitizeServices(data.Services || []),
    Faq: sanitizeFAQ(data.Faq || []),
    CphRates: sanitizeRates(data.CphRates || []),
    BillundRates: sanitizeRates(data.BillundRates || []),
    
    // Objects
    ContactCopenhagen: sanitizeContact(data.ContactCopenhagen || {}),
    ContactBillund: sanitizeContact(data.ContactBillund || {}),
    AboutUsBanner: sanitizeAboutUsBanner(data.AboutUsBanner || {}),
    AboutUsContent: sanitizeAboutUsContent(data.AboutUsContent || []),
    Certificates: sanitizeCertificates(data.Certificates || [])
  };

  // Update sanitized counts
  failures.Location.sanitized = sanitized.Location.length;
  failures.Services.sanitized = sanitized.Services.length;
  failures.Faq.sanitized = sanitized.Faq.length;
  failures.CphRates.sanitized = sanitized.CphRates.length;
  failures.BillundRates.sanitized = sanitized.BillundRates.length;
  failures.AboutUsContent.sanitized = sanitized.AboutUsContent.length;
  failures.Certificates.sanitized = sanitized.Certificates.length;

  // Report sanitization results
  console.log('[SECURITY] Data sanitization completed.');
  let hasFailures = false;
  
  Object.keys(failures).forEach(section => {
    const { original, sanitized } = failures[section];
    const failed = original - sanitized;
    if (failed > 0) {
      hasFailures = true;
      console.warn(`[SECURITY] ${section}: ${failed} item(s) failed sanitization (${original} → ${sanitized})`);
    }
  });

  if (!hasFailures) {
    console.log('[SECURITY] All data sanitized successfully with no failures.');
  }

  return sanitized;
};
